import {
    AddNoteIcon,
    AtIcon,
    CalendarIcon,
    CapacityIcon,
    CircleCrossIcon,
    EditNoteIcon,
    EmailIcon,
    ExportIcon,
    MenuIcon,
    PlusIcon,
    PrintIcon,
    RosterIcon,
    SendIcon,
    TrashIcon,
    UserAddIcon,
    UserSubtractIcon,
    WaitlistIcon,
    WifiIcon,
} from "@xola/icons";
import { Button, flash } from "@xola/ui-kit";
import clsx from "clsx";
import {
    getDraftReasons,
    isEquipmentBased,
    isPast,
    ItemActionBar,
    ItemActionOption,
    now,
    PurchaseActionOption,
    RosterActionOption,
} from "common";
import { isDraft } from "immer";
import { uniqueId } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { useMutation } from "react-query";
import { useHistory, useParams } from "react-router-dom";
import { Event, Experience, ExperiencePurchaseItem, Permission, Timeslot } from "types";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { useResource } from "../../../hooks/useResource";
import { canStartVirtualMeeting } from "../../../models/Event";
import { isVirtualExperience } from "../../../models/Experience";
import { getId, getIsTimeslotBookable } from "../../../models/Timeslot";
import { useDashboardFilterStore } from "../../../stores/dashboardFilterStore";
import { DashboardMode } from "../../../types/Dashboard";
import { ItemActionMenu } from "../../Common/ItemActionMenu";
import { PromptModal } from "../../Common/PromptModal";
import { CancelEventBookingsModal } from "../../Event/CancelEventBookingsModal";
import { CreateEventNoteModal } from "../../Event/CreateEventNoteModal";
import { EditEventCapacityModal } from "../../Event/EditEventCapacityModal";
import { EventChangeArrivalModal } from "../../Event/EventChangeArrivalModal";
import { EventNotesModal } from "../../Event/EventNotesModal";
import { ExportRosterModal } from "../../Event/ExportRosterModal";
import { JoinWaitlistModal } from "../../Event/JoinWaitlistModal";
import { MessageEventModal } from "../../Event/MessageEventModal";
import { Loader } from "../../Loader";
import { PurchaseChangeArrivalModal } from "../../PurchaseModification/PurchaseChangeArrivalModal";
import { PastBookingWarningModal } from "../../PurchasesMenu/PastBookingWarningModal";
import { AssignGuideModal } from "../Resources/Guides/AssignGuideModal";
import { UnassignEventGuidePrompt } from "../Resources/Guides/UnassignEventGuides";
import { usePurchase } from "@/hooks/resources/usePurchase";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { makeExperiencePurchaseItem } from "@/models/CreatePurchaseState";
import { CreatePurchaseStep, useCreatePurchaseStore } from "@/stores/createPurchaseStore";
import { useRosterDrawerStore } from "@/stores/rosterDrawerStore";

export type EventMenuAction = RosterActionOption | PurchaseActionOption;

interface EventActionsMenuProps {
    timeslot: Timeslot;
    className?: string;
    forceVertical?: boolean;
    isDashboardRosterView?: boolean;
    onClose?: (action?: EventMenuAction) => void;
    onPrintRosterClick?: () => void;
    children?: React.ReactNode;
}

export const EventActionsMenu = ({
    timeslot,
    forceVertical,
    isDashboardRosterView = false,
    className,
    onClose,
    onPrintRosterClick,
    children,
}: EventActionsMenuProps) => {
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();
    const history = useHistory();
    const seller = useSeller();
    const { filters } = useDashboardFilterStore((state) => state);

    const isListMode = filters.mode === DashboardMode.LIST;
    const urlParams = useParams<{ timeslotId?: string }>();

    const popoverParentId = uniqueId("popoverParentId");

    const { mutateAsync: sendRosterEmail } = useMutation(async () => {
        await api.get(`/events/${timeslot.event?.id}/email/roster`);
    });

    const { data: event, isLoading: isLoadingEvent } = useResource<Event>(["events", timeslot.event?.id]);
    const { data: experience } = useResource<Experience>(["experiences", timeslot?.product]);
    const { mutateAsync: deleteEvent } = useMutation(async () => {
        return api.delete(`/events/${timeslot.event?.id}`);
    });

    const { data: purchaseItems, isLoading: isLoadingPurchases } = useAutoFetchCollection<ExperiencePurchaseItem>(
        "purchaseItems",
        { event: timeslot.event?.id, expand: "waiver,membershipPurchaseItem,purchaseRuleLink,purchaseRule" },
        { enabled: !!timeslot.event?.id },
    );

    const { data: purchase } = usePurchase(purchaseItems[0]?.purchase.id);

    const hasMultipleReservations = purchaseItems.length > 1 && !isLoadingPurchases;

    const [selectedOption, setSelectedOption] = useState<RosterActionOption | null>(null);
    const [remainingAction, setRemainingAction] = useState<RosterActionOption | null>(null);
    const [warningModalOpen, setWarningModalOpen] = useState(false);
    // Keep previous event in case after arrival change to unassign guides
    const [previousEvent, setPreviousEvent] = useState<Event | null>(null);

    const { addItem, resetState, setSeller } = useCreatePurchaseStore.getState().actions;

    const { canWaitlist } = timeslot;

    const draftReasons = experience ? getDraftReasons(experience) : [];
    const isDraftExperience = isDraft(experience) || draftReasons.length > 0;

    const isBookingDisabled = !getIsTimeslotBookable(seller, timeslot, experience) || isDraftExperience;
    const isWaitlistDisabled = !timeslot.canWaitlist || isPast(timeslot.arrivalDatetime);
    const showNotesButton = timeslot.noteCount > 0;
    const hasNoBookingOrEvent = !event || (purchaseItems.length === 0 && !isLoadingPurchases);
    const isVirtual = (!!event?.virtualMeeting || isVirtualExperience(experience)) ?? false;

    const handleButtonClick = (action: EventMenuAction) => {
        if (action === RosterActionOption.OPEN_ROSTER) {
            const rosterPath = `/dashboard/${getId(timeslot)}`;
            history.push(rosterPath);
            return;
        }

        if (action === RosterActionOption.PRINT_ROSTER) {
            onPrintRosterClick?.();

            // Printing only works from the roster page
            const rosterPath = `/dashboard/${getId(timeslot)}`;
            if (history.location.pathname === rosterPath) {
                setTimeout(() => {
                    /** We need to perform print after a little delay, so the state and content needed to be print, would have rendered */
                    window.print();
                }, 200);
            } else {
                history.push(`/dashboard/${getId(timeslot)}`, { print: true });
            }

            return;
        }

        if (action === RosterActionOption.MAKE_PURCHASE) {
            const arrival = { date: timeslot.date, time: timeslot.time };
            if (experience) {
                // reset it first to clear any leftover
                resetState();
                setSeller(experience.seller.id);
                addItem(makeExperiencePurchaseItem(experience, arrival, timeslot));
                useCreatePurchaseStore.setState({ step: CreatePurchaseStep.CHECKOUT });
                history.push("/cart");
            }

            return;
        }

        // Before we open modal from action we must check if it's needed to open the warning modal
        const excludedPastDateActions = [
            RosterActionOption.EMAIL_ROSTER,
            RosterActionOption.EXPORT_ROSTER,
            RosterActionOption.MESSAGE,
        ];
        if (
            !excludedPastDateActions.includes(action as RosterActionOption) &&
            timeslot.arrivalDatetime.isBefore(now())
        ) {
            setRemainingAction(action as RosterActionOption);
            setWarningModalOpen(true);
            onClose?.(action);
            return;
        }

        setSelectedOption(action as RosterActionOption);
        onClose?.(action);
    };

    const resetSelectedOption = (e?: any) => {
        e?.stopPropagation?.();
        setSelectedOption(null);
    };

    const closeRosterAndResetSelectedOption = useCallback((shouldCloseRoster: boolean) => {
        resetSelectedOption();
        if (shouldCloseRoster) {
            useRosterDrawerStore.getState().setClose();
        }
    }, []);

    const handleCloseModalWithRedirect = (e?: any, redirectUrl?: string) => {
        e?.stopPropagation?.();
        setSelectedOption(null);

        if (redirectUrl && isDashboardRosterView) {
            history.push(redirectUrl);
        }
    };

    const handleEmailRoster = async () => {
        await sendRosterEmail();
        resetSelectedOption();
        flash.show({ text: "Roster email sent" });
    };

    const handleDeleteEvent = async () => {
        try {
            await deleteEvent();
            invalidate("events");
            // adding a timeout to avoid any calls being made immediately to timeslots which might fetch old data and cache it. Refer X2-11441 for more details.
            // if there are any issues in UI cache later, invalidate the timeslot query cache in UI with a stale time or might have to be handled in backend with pusher service to handle delete events.
            setTimeout(() => {
                invalidate("timeslots");
            }, 1000);
            resetSelectedOption();
            history.push("/dashboard");
        } catch {
            flash.danger("Sorry, we were unable to delete this trip");
            resetSelectedOption();
        }
    };

    const handleCreatedEvent = (eventId: ID) => {
        if (isListMode && !!urlParams.timeslotId) {
            const newId = getId({
                date: timeslot.date,
                time: timeslot.time,
                product: timeslot.product,
                event: { id: eventId },
            });
            history.push(`/dashboard/${newId}`);
        }
    };

    const sendMessageButton = {
        title: forceVertical ? "Send Message" : "Message",
        icon: <SendIcon />,
        action: RosterActionOption.MESSAGE,
        permission: Permission.SEND_EVENT_MESSAGE,
        isLoading: isLoadingPurchases,
        disabled: hasNoBookingOrEvent,
    };

    const addNoteButton = {
        title: forceVertical ? "Add Event Note" : "Notes",
        icon: showNotesButton ? <EditNoteIcon /> : <AddNoteIcon />,
        action: RosterActionOption.ADD_EVENT_NOTE,
        permission: Permission.ADD_EVENT_NOTE,
        disabled: !event || isDashboardRosterView,
    };

    const changeArrivalButton = {
        title: "Change Arrival",
        icon: <CalendarIcon />,
        action: RosterActionOption.CHANGE_EVENT_ARRIVAL,
        permission: Permission.MODIFY_EVENT,
        isLoading: isLoadingPurchases,
        disabled: hasNoBookingOrEvent,
    };

    const cancelBookingButton = {
        title: "Cancel Reservations",
        icon: <CircleCrossIcon />,
        action: RosterActionOption.CANCEL_BOOKING,
        permission: Permission.MODIFY_BOOKING,
        isLoading: isLoadingPurchases,
        disabled: hasNoBookingOrEvent,
    };

    const emailRosterButton = {
        title: forceVertical ? "Email" : "Email Roster",
        icon: forceVertical ? <AtIcon /> : <EmailIcon />,
        action: RosterActionOption.EMAIL_ROSTER,
        permission: Permission.EMAIL_ROSTER,
        isLoading: isLoadingPurchases,
        disabled: hasNoBookingOrEvent,
    };

    const exportRosterButton = {
        title: forceVertical ? "Export" : "Export Roster",
        icon: <ExportIcon />,
        action: RosterActionOption.EXPORT_ROSTER,
        isLoading: isLoadingPurchases,
        disabled: hasNoBookingOrEvent,
    };

    const printRosterButton = {
        title: forceVertical ? "Print" : "Print Roster",
        icon: <PrintIcon />,
        action: RosterActionOption.PRINT_ROSTER,
        isLoading: isLoadingPurchases,
        disabled: hasNoBookingOrEvent,
    };

    const joinWaitlistButton = {
        title: "Join Waitlist",
        icon: <MenuIcon />,
        action: RosterActionOption.JOIN_WAITLIST,
        permission: Permission.MODIFY_BOOKING_LITE,
        exclude: isWaitlistDisabled,
        disabled: !event,
    };

    const adjustCapacityButton = {
        title: "Adjust Capacity",
        icon: <CapacityIcon />,
        action: RosterActionOption.ADJUST_CAPACITY,
        permission: Permission.MODIFY_EQUIPMENT_CAPACITY,
        exclude: experience ? isEquipmentBased(experience) : false,
    };

    const assignGuidesButton = {
        title: "Assign Guides",
        icon: <UserAddIcon />,
        action: RosterActionOption.ASSIGN_GUIDE,
        permission: Permission.RESOURCES_GUIDES_ALL_VIEW,
    };

    const unassignGuidesButton = {
        title: "Unassign Guides",
        icon: <UserSubtractIcon />,
        action: RosterActionOption.UNASSIGN_GUIDE,
        disabled: !(event && event?.guides.length > 0),
        permission: Permission.RESOURCES_GUIDES_ALL_VIEW,
    };

    const waitlistButton = {
        title: "Waitlist",
        icon: <WaitlistIcon />,
        color: "caution",
        exclude: !(isBookingDisabled && canWaitlist),
        permission: Permission.MODIFY_EVENT,
        disabled: isWaitlistDisabled,
        action: RosterActionOption.JOIN_WAITLIST,
    };

    const makePurchaseButton = {
        title: "Purchase",
        icon: <PlusIcon />,
        color: "success",
        exclude: isBookingDisabled && canWaitlist,
        permission: Permission.MODIFY_EVENT,
        disabled: isBookingDisabled,
        action: RosterActionOption.MAKE_PURCHASE,
    };

    const filterOptions = (items: ItemActionOption[]) => {
        return items.filter((item) => {
            return item.permission ? isGranted(item.permission) && !item.exclude : true;
        });
    };

    const rosterGroupButton: ItemActionOption = {
        title: "Roster",
        icon: <RosterIcon />,
        action: RosterActionOption.OPEN_ROSTER,
        suboptions: filterOptions([exportRosterButton, printRosterButton, emailRosterButton]),
        disabled: hasNoBookingOrEvent,
    };

    const deleteEventButton: ItemActionOption = {
        title: "Delete",
        icon: <TrashIcon />,
        exclude: !event?.manual || Math.max(event.quantity.reserved, event.quantity.confirmed) > 0,
        action: RosterActionOption.DELETE_EVENT,
        permission: Permission.MODIFY_EVENT,
    };

    const handleStartVirtualExperience = (e: any) => {
        const sellerUrl = event?.virtualMeeting.sellerUrl;
        if (sellerUrl) {
            window.open(sellerUrl);
        }

        resetSelectedOption(e);
        onClose?.();
    };

    const showStartVirtualMeeting = canStartVirtualMeeting(event, experience);

    useEffect(() => {
        if (selectedOption === RosterActionOption.EMAIL_ROSTER) {
            handleEmailRoster();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOption]);

    if (isLoadingEvent) {
        return <Loader />;
    }

    return (
        <div id={popoverParentId}>
            {forceVertical ? (
                <>
                    <ItemActionMenu
                        actions={filterOptions([
                            sendMessageButton,
                            addNoteButton,
                            changeArrivalButton,
                            cancelBookingButton,
                            rosterGroupButton,
                            joinWaitlistButton,
                            adjustCapacityButton,
                            deleteEventButton,
                            assignGuidesButton,
                            unassignGuidesButton,
                        ])}
                        onClick={handleButtonClick}
                    />
                    {isVirtual && showStartVirtualMeeting ? (
                        <div className="flex w-full p-4">
                            <Button
                                icon={<WifiIcon />}
                                className="m-auto"
                                size="small"
                                onClick={(e: any) => handleStartVirtualExperience(e)}
                            >
                                Start Online Experience
                            </Button>
                        </div>
                    ) : null}
                    {children}
                </>
            ) : (
                <ItemActionBar
                    actions={filterOptions([
                        sendMessageButton,
                        changeArrivalButton,
                        cancelBookingButton,
                        emailRosterButton,
                        exportRosterButton,
                        printRosterButton,
                        waitlistButton,
                        makePurchaseButton,
                        deleteEventButton,
                    ])}
                    className={clsx("rounded border border-gray-light px-6", className)}
                    popoverParentId={popoverParentId}
                    onButtonClick={handleButtonClick}
                >
                    {children}
                </ItemActionBar>
            )}

            {event ? (
                <>
                    <MessageEventModal
                        eventId={event.id}
                        isOpen={selectedOption === RosterActionOption.MESSAGE}
                        onClose={resetSelectedOption}
                    />

                    <CreateEventNoteModal
                        events={event ? [event] : []}
                        timeslot={timeslot}
                        isOpen={selectedOption === RosterActionOption.ADD_EVENT_NOTE}
                        onClose={resetSelectedOption}
                    />

                    <ExportRosterModal
                        eventId={event.id}
                        filters={{ format: "roster" }}
                        isOpen={selectedOption === RosterActionOption.EXPORT_ROSTER}
                        onClose={resetSelectedOption}
                    />

                    <EventChangeArrivalModal
                        timeslot={timeslot}
                        isOpen={selectedOption === RosterActionOption.CHANGE_EVENT_ARRIVAL}
                        onClose={handleCloseModalWithRedirect}
                    />

                    {purchaseItems.length > 0 && purchase ? (
                        <PurchaseChangeArrivalModal
                            purchase={purchase}
                            item={purchaseItems[0]}
                            isOpen={
                                !hasMultipleReservations &&
                                selectedOption === RosterActionOption.CHANGE_PURCHASE_ARRIVAL
                            }
                            onClose={handleCloseModalWithRedirect}
                            onContinue={(event: Event | null) => {
                                setPreviousEvent(event ?? null);
                                setSelectedOption(RosterActionOption.UNASSIGN_GUIDE);
                            }}
                        />
                    ) : null}

                    <CancelEventBookingsModal
                        timeslot={timeslot}
                        isOpen={selectedOption === RosterActionOption.CANCEL_BOOKING}
                        onClose={closeRosterAndResetSelectedOption}
                    />

                    <JoinWaitlistModal
                        timeslot={timeslot}
                        isOpen={selectedOption === RosterActionOption.JOIN_WAITLIST}
                        onClose={resetSelectedOption}
                    />

                    <EventNotesModal
                        eventId={event.id}
                        isOpen={selectedOption === RosterActionOption.EVENT_NOTES}
                        onClose={resetSelectedOption}
                    />

                    <UnassignEventGuidePrompt
                        isOpen={selectedOption === RosterActionOption.UNASSIGN_GUIDE}
                        events={previousEvent ?? [event]}
                        onClose={() => {
                            resetSelectedOption();
                            setPreviousEvent(null);
                        }}
                    />

                    <PromptModal
                        title="Delete Event?"
                        isOpen={selectedOption === RosterActionOption.DELETE_EVENT}
                        onConfirm={handleDeleteEvent}
                        onClose={resetSelectedOption}
                    >
                        Are you sure you want to delete this event?
                    </PromptModal>
                </>
            ) : null}

            {/* The modals, that can create an event when it doesn't exist in the timeslot */}

            <AssignGuideModal
                events={event ? [event] : undefined}
                timeslots={event ? [] : [timeslot]}
                isVisible={selectedOption === RosterActionOption.ASSIGN_GUIDE}
                onClose={resetSelectedOption}
            />

            <EditEventCapacityModal
                timeslot={timeslot}
                isOpen={selectedOption === RosterActionOption.ADJUST_CAPACITY}
                onCreatedEvent={handleCreatedEvent}
                onClose={resetSelectedOption}
            />

            <PastBookingWarningModal
                isOpen={warningModalOpen}
                onClose={() => setWarningModalOpen(false)}
                onContinue={() => {
                    setWarningModalOpen(false);
                    setSelectedOption(remainingAction);
                }}
            />
        </div>
    );
};
